import { useState } from 'react';
import {
  DEFAULT_FOLLOWUP_LABEL,
  MAX_FOLLOWUP_LABEL_LENGTH,
  MAX_FOLLOWUP_MESSAGE_LENGTH,
  MAX_FOLLOWUP_URL_LENGTH,
  isEmptyFollowUp,
  type FollowUp,
} from '@floorpoll/shared';

interface Props {
  followUp: FollowUp | null | undefined;
  optionLabel: string;
  disabled: boolean;
  onChange: (followUp: FollowUp | null) => void;
}

/**
 * Collapsed by default, and that is a requirement rather than a preference: a
 * screen holds up to 3 questions of 10 options, and three always-visible fields
 * on each would bury the option text they belong to.
 *
 * Collapsed, it is a chip under its option rather than a button beside it — at
 * ten options a row of pill buttons competes with the answers themselves, which
 * are the thing being written. Open, it is an inset held by a single rule: the
 * bordered, filled box it used to be made a fourth nested surface inside a card
 * that already had three.
 */
export function FollowUpEditor({ followUp, optionLabel, disabled, onChange }: Props) {
  const filled = !isEmptyFollowUp(followUp);
  const [open, setOpen] = useState(filled);

  if (!open) {
    return (
      <button
        type="button"
        className={`followup-toggle${filled ? ' followup-toggle-filled' : ''}`}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        {filled ? '↗ Follow-up' : 'Add follow-up'}
      </button>
    );
  }

  const patch = (change: Partial<FollowUp>) => onChange({ ...(followUp ?? {}), ...change });

  return (
    <div className="followup-editor">
      <p className="eyebrow followup-eyebrow">Follow-up</p>

      <div className="field">
        <label htmlFor={`fu-msg-${optionLabel}`}>Message</label>
        <textarea
          id={`fu-msg-${optionLabel}`}
          rows={2}
          maxLength={MAX_FOLLOWUP_MESSAGE_LENGTH}
          value={followUp?.message ?? ''}
          disabled={disabled}
          placeholder="Shown to people who pick this option"
          onChange={(e) => patch({ message: e.target.value })}
        />
      </div>

      {/* Paired: a label with no link is a validation error (`validateFollowUp`),
          so the two fields are one decision and sit on one line. */}
      <div className="followup-link-fields">
        <div className="field">
          <label htmlFor={`fu-url-${optionLabel}`}>Link</label>
          <input
            id={`fu-url-${optionLabel}`}
            type="url"
            maxLength={MAX_FOLLOWUP_URL_LENGTH}
            value={followUp?.url ?? ''}
            disabled={disabled}
            placeholder="https://example.org"
            onChange={(e) => patch({ url: e.target.value })}
          />
        </div>

        <div className="field">
          <label htmlFor={`fu-label-${optionLabel}`}>Link label</label>
          <input
            id={`fu-label-${optionLabel}`}
            maxLength={MAX_FOLLOWUP_LABEL_LENGTH}
            value={followUp?.linkLabel ?? ''}
            disabled={disabled}
            placeholder={DEFAULT_FOLLOWUP_LABEL}
            onChange={(e) => patch({ linkLabel: e.target.value })}
          />
        </div>
      </div>

      {/* Collapse only hides the editor — the follow-up is untouched and
          reopens exactly as left. Remove is the only control that discards
          it. Keep that split in the handlers, not just the labels: a future
          merge of these two buttons would make hiding destructive. */}
      <div className="followup-actions">
        <button
          type="button"
          className="text-button"
          disabled={disabled}
          onClick={() => setOpen(false)}
        >
          Collapse
        </button>
        <button
          type="button"
          className="text-button text-button-destroy"
          disabled={disabled}
          onClick={() => {
            onChange(null);
            setOpen(false);
          }}
        >
          Remove follow-up
        </button>
      </div>
    </div>
  );
}
